import { useEffect, useRef, useState } from "react";
import { Bot, Eraser, Mic, Send, UserRound, Volume2, VolumeX } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy, GUIDANCE_TEXT } from "../content";
import { routeV2AssistantIntent, V2_ASSISTANT_INTENTS } from "../lib/v2AssistantRouter";
import {
  answerAvailableFoods,
  answerDashboardQuestion,
  answerFoodSearch,
  answerHistoryQuestion,
  buildAppHelpAnswer,
  buildAssistantContext,
  buildBlockedWriteResponse,
  buildKnowledgeAnswer,
  buildReminderHelpAnswer,
  buildUrgentHealthResponse,
  getReadOnlyAssistantFallbackMessage,
  sanitizeAssistantText,
  searchAssistantFoods,
  searchKnowledgeItems,
} from "../lib/v2AssistantUtils";
import {
  canUseServerAi,
  countServerAiCall,
  getAiLimitReachedMessage,
  V2_AI_USER_TYPES,
} from "../lib/v2AiUsageUtils";
import { formatServing } from "../lib/v2FoodUtils";
import {
  chooseSpeechSynthesisVoice,
  getSpeechRecognitionConstructor,
  getSpeechRecognitionLanguage,
  getSpeechSynthesisLanguage,
  getVoiceInputErrorMessage,
  getVoiceInputUnsupportedMessage,
  getVoiceOutputUnsupportedMessage,
  isSpeechRecognitionSupported,
  isSpeechSynthesisSupported,
  prepareSpokenAnswer,
  sanitizeVoiceTranscript,
} from "../lib/v2VoiceUtils";

const englishQuickQuestions = [
  "What foods did I log today?",
  "Show my remaining potassium today",
  "Search for apple",
  "What foods are available?",
  "Explain my dashboard summary",
  "What should I watch today?",
];

const arabicQuickQuestions = [
  "ماذا أكلت اليوم؟",
  "كم بقي لي من البوتاسيوم اليوم؟",
  "ابحث عن التفاح",
  "ما الأطعمة المتاحة؟",
  "اشرح ملخص اليوم",
  "ماذا يجب أن أراقب اليوم؟",
];

const initialMessages = {
  en: [
  {
    id: "assistant-welcome",
    role: "assistant",
    text: "Ask about foods, dashboard numbers, food history, reminders, or approved knowledge.",
  },
  ],
  ar: [
    {
      id: "assistant-welcome-ar",
      role: "assistant",
      text: "اسأل عن الأطعمة، أرقام اليوم، السجل، التذكيرات، أو المعرفة المعتمدة.",
    },
  ],
};

const MAX_STORED_ASSISTANT_MESSAGES = 100;

function getAssistantStorageKey({ isGuest, userId }) {
  if (userId) return `ddp-v2-assistant-messages-${userId}`;
  if (isGuest) return "ddp-v2-assistant-messages-guest";
  return "";
}

function readStoredAssistantMessages(storageKey) {
  if (!storageKey) return null;

  try {
    const storedMessages = JSON.parse(window.sessionStorage.getItem(storageKey) || "null");
    if (!Array.isArray(storedMessages) || storedMessages.length === 0) return null;

    const safeMessages = storedMessages
      .filter((message) =>
        message &&
        typeof message.id === "string" &&
        (message.role === "user" || message.role === "assistant") &&
        typeof message.text === "string"
      )
      .slice(-MAX_STORED_ASSISTANT_MESSAGES)
      .map((message) => ({
        id: message.id,
        role: message.role,
        text: sanitizeAssistantText(message.text),
      }));

    return safeMessages.length > 0 ? safeMessages : null;
  } catch {
    return null;
  }
}

function getInitialAssistantMessages(lang, storageKey) {
  return readStoredAssistantMessages(storageKey) || initialMessages[lang] || initialMessages.en;
}

const assistantUiCopy = {
  en: {
    description: "Search foods, review today, and ask questions using the app data on this device.",
    clearConversation: "Clear conversation",
    thinking: "Processing...",
    askPlaceholder: "Ask a question",
    send: "Send",
    speakAnswers: "Speak answers",
    listening: "Listening…",
    processing: "Processing",
    speaking: "Speaking",
    voiceUnsupported: "Voice unsupported",
    startVoiceInput: "Start voice input",
    stopVoiceInput: "Stop voice input",
    stopSpeaking: "Stop speaking",
  },
  ar: {
    description: "ابحث عن الأطعمة، راجع ملخص اليوم، واسأل باستخدام بيانات التطبيق على هذا الجهاز.",
    clearConversation: "مسح المحادثة",
    thinking: "جاري المعالجة...",
    askPlaceholder: "اكتب سؤالك",
    send: "إرسال",
    speakAnswers: "تشغيل الردود الصوتية",
    listening: "جارٍ الاستماع…",
    processing: "جاري المعالجة",
    speaking: "يتحدث",
    voiceUnsupported: "الصوت غير مدعوم",
    startVoiceInput: "ابدأ الإدخال الصوتي",
    stopVoiceInput: "إيقاف الإدخال الصوتي",
    stopSpeaking: "إيقاف الصوت",
  },
};

export default function V2Assistant() {
  const { lang } = useLang();
  const { isGuest, session, settings } = useV2Session();
  const userId = session?.user?.id;
  const assistantStorageKey = getAssistantStorageKey({ isGuest, userId });
  const copy = getV2Copy(lang);
  const ui = assistantUiCopy[lang] || assistantUiCopy.en;
  const [messages, setMessages] = useState(() => getInitialAssistantMessages(lang, assistantStorageKey));
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isVoiceProcessing, setIsVoiceProcessing] = useState(false);
  const [speakAnswers, setSpeakAnswers] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const submitTranscriptOnEndRef = useRef(false);
  const intentionalAbortRef = useRef(false);
  const pressActiveRef = useRef(false);
  const activePointerIdRef = useRef(null);
  const suppressClickRef = useRef(false);
  const speakingUtteranceRef = useRef(null);
  const loadedStorageKeyRef = useRef(assistantStorageKey);
  const skipNextPersistRef = useRef(false);
  const quickQuestions = lang === "ar" ? arabicQuickQuestions : englishQuickQuestions;
  const isArabic = lang === "ar";
  const voiceInputStatus = isListening
    ? ui.listening
    : isVoiceProcessing
      ? ui.processing
      : voiceNotice;

  useEffect(() => {
    return () => {
      pressActiveRef.current = false;
      activePointerIdRef.current = null;
      submitTranscriptOnEndRef.current = false;
      intentionalAbortRef.current = true;
      recognitionRef.current?.abort?.();
      recognitionRef.current = null;
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    const previousStorageKey = loadedStorageKeyRef.current;
    if (previousStorageKey === assistantStorageKey) return;

    const storedMessages = readStoredAssistantMessages(assistantStorageKey);
    loadedStorageKeyRef.current = assistantStorageKey;

    if (storedMessages) {
      skipNextPersistRef.current = true;
      setMessages(storedMessages);
      return;
    }

    if (!assistantStorageKey || previousStorageKey) {
      skipNextPersistRef.current = true;
      setMessages(initialMessages[lang] || initialMessages.en);
      return;
    }

    skipNextPersistRef.current = false;
  }, [assistantStorageKey, lang]);

  useEffect(() => {
    if (!assistantStorageKey) return;
    if (loadedStorageKeyRef.current !== assistantStorageKey) return;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    try {
      window.sessionStorage.setItem(
        assistantStorageKey,
        JSON.stringify(messages.slice(-MAX_STORED_ASSISTANT_MESSAGES))
      );
    } catch {
      // Session storage is optional; the in-memory conversation remains usable.
    }
  }, [assistantStorageKey, messages]);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const question = input.trim();
    if (!question || isSending) return;

    setInput("");
    await handleQuestion(question);
  };

  const handleQuestion = async (question) => {
    const currentAssistantContext = buildAssistantContext({ isGuest, settings });
    const userMessage = createMessage("user", question);
    setMessages((current) => [...current, userMessage]);
    setIsSending(true);

    const localAnswer = getLocalAssistantAnswer(question, currentAssistantContext);
    if (localAnswer) {
      addAssistantAnswer(localAnswer);
      setIsSending(false);
      return;
    }

    const serverAnswer = await requestServerAssistant(question, currentAssistantContext, { isGuest, settings });
    addAssistantAnswer(serverAnswer);
    setIsSending(false);
  };

  const addAssistantAnswer = (answer) => {
    const safeAnswer = sanitizeAssistantText(answer);
    setMessages((current) => [...current, createMessage("assistant", safeAnswer)]);
    speakAssistantAnswer(safeAnswer);
  };

  const handleClear = () => {
    setMessages(initialMessages[lang] || initialMessages.en);
    setInput("");
    setVoiceNotice("");
    stopSpeaking();
  };

  const startListening = () => {
    if (recognitionRef.current || isSending) return;
    if (!isSpeechRecognitionSupported()) {
      setVoiceNotice(getVoiceInputUnsupportedMessage(lang));
      pressActiveRef.current = false;
      activePointerIdRef.current = null;
      return;
    }

    const SpeechRecognition = getSpeechRecognitionConstructor();
    const recognition = new SpeechRecognition();
    recognition.lang = getSpeechRecognitionLanguage(lang);
    recognition.continuous = true;
    recognition.interimResults = true;
    finalTranscriptRef.current = "";
    submitTranscriptOnEndRef.current = false;
    intentionalAbortRef.current = false;

    recognition.onstart = () => {
      setVoiceNotice("");
      setIsListening(true);
      setIsVoiceProcessing(false);
    };

    recognition.onresult = (event) => {
      let finalTranscript = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        if (result.isFinal) {
          finalTranscript += result[0]?.transcript || "";
        }
      }

      const transcript = sanitizeVoiceTranscript(finalTranscript);
      if (transcript) {
        finalTranscriptRef.current = sanitizeVoiceTranscript(
          `${finalTranscriptRef.current} ${transcript}`
        );
      }
    };

    recognition.onerror = (event) => {
      if (intentionalAbortRef.current && event?.error === "aborted") {
        setVoiceNotice("");
        setIsListening(false);
        setIsVoiceProcessing(false);
        return;
      }

      setVoiceNotice(getVoiceInputErrorMessage(lang, event?.error));
      pressActiveRef.current = false;
      activePointerIdRef.current = null;
      submitTranscriptOnEndRef.current = false;
      intentionalAbortRef.current = false;
      setIsListening(false);
      setIsVoiceProcessing(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      const shouldSubmit = submitTranscriptOnEndRef.current;
      const transcript = sanitizeVoiceTranscript(finalTranscriptRef.current);
      pressActiveRef.current = false;
      activePointerIdRef.current = null;
      submitTranscriptOnEndRef.current = false;
      intentionalAbortRef.current = false;
      setIsListening(false);
      setIsVoiceProcessing(false);
      recognitionRef.current = null;
      if (transcript) {
        if (shouldSubmit) {
          setInput("");
          handleQuestion(transcript);
        } else {
          setInput(transcript);
        }
      }
    };

    recognitionRef.current = recognition;
    setVoiceNotice("");
    setIsListening(true);
    setIsVoiceProcessing(true);
    try {
      recognition.start();
    } catch {
      setVoiceNotice(getVoiceInputErrorMessage(lang));
      pressActiveRef.current = false;
      activePointerIdRef.current = null;
      intentionalAbortRef.current = false;
      setIsListening(false);
      setIsVoiceProcessing(false);
      recognitionRef.current = null;
    }
  };

  const stopListening = ({ submit = true, abort = false } = {}) => {
    const recognition = recognitionRef.current;
    pressActiveRef.current = false;
    activePointerIdRef.current = null;
    submitTranscriptOnEndRef.current = submit;
    if (!recognition) {
      intentionalAbortRef.current = false;
      setIsListening(false);
      setIsVoiceProcessing(false);
      return;
    }

    setIsListening(false);
    setIsVoiceProcessing(true);
    try {
      if (abort) {
        submitTranscriptOnEndRef.current = false;
        intentionalAbortRef.current = true;
        recognition.abort();
      } else {
        intentionalAbortRef.current = false;
        recognition.stop();
      }
    } catch {
      submitTranscriptOnEndRef.current = false;
      intentionalAbortRef.current = false;
      setIsVoiceProcessing(false);
      recognitionRef.current = null;
    }
  };

  const releasePointerCapture = (event) => {
    if (event?.pointerId == null) return;
    try {
      if (event.currentTarget?.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Some emulated/mobile browsers report capture inconsistently.
    }
  };

  const beginPushToTalk = (event) => {
    event?.preventDefault();
    if (pressActiveRef.current || recognitionRef.current || isSending) return;
    if (event?.pointerId != null) {
      activePointerIdRef.current = event.pointerId;
      try {
        event.currentTarget?.setPointerCapture?.(event.pointerId);
      } catch {
        activePointerIdRef.current = null;
      }
    }
    pressActiveRef.current = true;
    startListening();
  };

  const endPushToTalk = (event, options = {}) => {
    event?.preventDefault();
    if (
      event?.pointerId != null &&
      activePointerIdRef.current != null &&
      event.pointerId !== activePointerIdRef.current
    ) {
      return;
    }
    if (!pressActiveRef.current && !recognitionRef.current) return;
    suppressClickRef.current = true;
    releasePointerCapture(event);
    stopListening(options);
  };

  const handleLostPointerCapture = (event) => {
    if (!pressActiveRef.current) return;
    endPushToTalk(event, { submit: false, abort: true });
  };

  const handleVoiceActivationClick = (event) => {
    if (event.detail !== 0) return;
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (pressActiveRef.current || recognitionRef.current) {
      endPushToTalk(event, { submit: true });
    } else {
      beginPushToTalk(event);
    }
  };

  const handleSpeakToggle = (checked) => {
    setSpeakAnswers(checked);
    if (!checked) {
      stopSpeaking();
      return;
    }

    if (!isSpeechSynthesisSupported()) {
      setSpeakAnswers(false);
      setVoiceNotice(getVoiceOutputUnsupportedMessage(lang));
    }
  };

  const speakAssistantAnswer = (answer) => {
    if (!speakAnswers) return;

    if (!isSpeechSynthesisSupported()) {
      setVoiceNotice(getVoiceOutputUnsupportedMessage(lang));
      setSpeakAnswers(false);
      return;
    }

    const spokenAnswer = prepareSpokenAnswer(answer);
    if (!spokenAnswer) return;

    const utterance = new window.SpeechSynthesisUtterance(spokenAnswer);
    utterance.lang = getSpeechSynthesisLanguage(lang);
    const voice = chooseSpeechSynthesisVoice(window, lang);
    if (voice) utterance.voice = voice;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      speakingUtteranceRef.current = null;
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      speakingUtteranceRef.current = null;
    };

    stopSpeaking();
    speakingUtteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    speakingUtteranceRef.current = null;
    setIsSpeaking(false);
  };

  return (
    <section className="mx-auto flex min-h-[calc(100vh-8rem)] max-w-6xl flex-col gap-5 pb-16">
      <header className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end" dir={isArabic ? "rtl" : "ltr"}>
        <div className="space-y-2">
          <p className="text-sm font-semibold text-primary">{copy.brand}</p>
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">{copy.assistant}</h1>
          <p className="max-w-3xl text-muted-foreground">
            {ui.description}
          </p>
        </div>
        <Button type="button" variant="outline" onClick={handleClear}>
          <Eraser className="h-4 w-4" />
          {ui.clearConversation}
        </Button>
      </header>

      <div className="rounded-lg border border-border bg-card p-4 shadow-sm" dir={isArabic ? "rtl" : "ltr"}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="grid w-full gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {quickQuestions.map((question) => (
              <Button
                key={question}
                type="button"
                variant="outline"
                size="sm"
                className={`h-auto min-h-10 justify-start whitespace-normal leading-5 ${lang === "ar" ? "text-right" : "text-left"}`}
                onClick={() => handleQuestion(question)}
                disabled={isSending}
              >
                {question}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 text-sm lg:justify-end">
            <label className="flex min-h-10 items-center gap-2 rounded-md border border-border bg-background px-3 py-2 font-medium text-foreground">
              <Switch checked={speakAnswers} onCheckedChange={handleSpeakToggle} aria-label={ui.speakAnswers} />
              {ui.speakAnswers}
            </label>
            {isSpeaking && (
              <Button type="button" variant="outline" size="sm" onClick={stopSpeaking} aria-label={ui.stopSpeaking}>
                <VolumeX className="h-4 w-4" />
                {ui.stopSpeaking}
              </Button>
            )}
          </div>
        </div>

        {isSpeaking && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {isSpeaking && <VoiceStateBadge label={ui.speaking} icon={<Volume2 className="h-3.5 w-3.5" />} />}
          </div>
        )}
      </div>

      <div className="flex min-h-[26rem] flex-1 flex-col rounded-lg border border-border bg-card shadow-sm">
        <div className="flex-1 space-y-4 overflow-y-auto p-4 md:p-5">
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} lang={lang} />
          ))}
          {isSending && (
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Bot className="h-4 w-4" />
              </span>
              {ui.thinking}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="border-t border-border p-4">
          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end" dir={isArabic ? "rtl" : "ltr"}>
            <Textarea
              value={input}
              onChange={(event) => setInput(event.target.value)}
              rows={3}
              placeholder={ui.askPlaceholder}
              className="resize-none"
            />
            <div className="grid grid-cols-[auto_1fr] gap-2 sm:flex sm:justify-end">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={`h-10 w-10 shrink-0 touch-none select-none p-0 transition-[color,background-color,border-color,box-shadow,opacity] ${
                  isListening
                    ? "border-primary bg-primary/10 text-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.16)]"
                    : isVoiceProcessing
                      ? "animate-pulse border-primary/60 text-primary"
                      : ""
                }`}
                onPointerDown={beginPushToTalk}
                onPointerUp={(event) => endPushToTalk(event, { submit: true })}
                onPointerCancel={(event) => endPushToTalk(event, { submit: false, abort: true })}
                onLostPointerCapture={handleLostPointerCapture}
                onContextMenu={(event) => event.preventDefault()}
                onDragStart={(event) => event.preventDefault()}
                onKeyDown={(event) => {
                  if ((event.key === "Enter" || event.key === " ") && !event.repeat) {
                    beginPushToTalk(event);
                  }
                }}
                onKeyUp={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    endPushToTalk(event, { submit: true });
                  }
                }}
                onClick={handleVoiceActivationClick}
                disabled={isSending}
                draggable={false}
                style={{ WebkitTouchCallout: "none" }}
                aria-label={isListening ? ui.stopVoiceInput : ui.startVoiceInput}
                title={isListening ? ui.stopVoiceInput : ui.startVoiceInput}
                data-testid="v2-push-to-talk"
                data-voice-state={isListening ? "listening" : isVoiceProcessing ? "processing" : "idle"}
              >
                <Mic className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button type="submit" className="min-h-10" disabled={!input.trim() || isSending}>
                <Send className="h-4 w-4" />
                {ui.send}
              </Button>
            </div>
          </div>
          <div
            className={`mt-2 min-h-5 text-sm leading-5 ${
              voiceNotice ? "text-destructive" : "text-muted-foreground"
            }`}
            aria-live="polite"
            data-testid="v2-voice-status"
          >
            {voiceNotice === getVoiceInputUnsupportedMessage(lang) ||
            voiceNotice === getVoiceOutputUnsupportedMessage(lang)
              ? `${ui.voiceUnsupported}: ${voiceInputStatus}`
              : voiceInputStatus}
          </div>
        </form>
      </div>

      <div className="rounded-lg border border-border bg-background px-4 py-3 text-center text-sm font-medium shadow-sm">
        {lang === "ar" ? copy.guidance : GUIDANCE_TEXT}
      </div>
    </section>
  );
}

function VoiceStateBadge({ label, icon }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-md bg-primary/10 px-3 py-1.5 font-medium text-primary">
      {icon || <Mic className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

function getLocalAssistantAnswer(question, context) {
  const route = routeV2AssistantIntent(question);

  switch (route.intent) {
    case V2_ASSISTANT_INTENTS.blockedWriteAction:
      return buildBlockedWriteResponse(route.language);
    case V2_ASSISTANT_INTENTS.urgentHealthMessage:
      return buildUrgentHealthResponse();
    case V2_ASSISTANT_INTENTS.foodSearch:
      return /available foods?|foods available|food list|شو في|أطعمة|اطعمة/i.test(question)
        ? answerAvailableFoods(context)
        : answerFoodSearch(question, context);
    case V2_ASSISTANT_INTENTS.dashboardSummary:
      return answerDashboardQuestion(question, context, { language: route.language });
    case V2_ASSISTANT_INTENTS.historySummary:
      return answerHistoryQuestion(question, context, { language: route.language });
    case V2_ASSISTANT_INTENTS.knowledgeSearch:
      return buildKnowledgeAnswer(question, context);
    case V2_ASSISTANT_INTENTS.reminderHelp:
      return buildReminderHelpAnswer();
    case V2_ASSISTANT_INTENTS.appHelp:
      return buildAppHelpAnswer(question);
    default:
      return "";
  }
}

async function requestServerAssistant(question, context, { isGuest = false, settings = {} } = {}) {
  const usageDecision = canUseServerAi({
    userType: isGuest ? V2_AI_USER_TYPES.guest : V2_AI_USER_TYPES.registered,
    appSettings: settings?.admin_app_settings || {},
  });

  if (!usageDecision.allowed) {
    return getAiLimitReachedMessage();
  }

  const selectedFoods = searchAssistantFoods(question, context.foods || [], { limit: 4 }).map((food) => ({
    name: food.display_name || food.name_en || food.name,
    category: food.category,
    type_label: food.type_label,
    serving: formatServing(food.quantity || food.serving_quantity, food.quantity_unit || food.serving_unit),
    potassium_mg: food.potassium_mg,
    phosphorus_mg: food.phosphorus_mg,
    sodium_mg: food.sodium_mg,
    protein_g: food.protein_g,
  }));
  const knowledgeSnippets = searchKnowledgeItems(question, context, { limit: 4 }).map((item) => ({
    title: item.title,
    category: item.category,
    content: item.content,
  }));

  try {
    const response = await fetch("/.netlify/functions/v2-readonly-assistant", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        context: {
          dashboard: {
            date: context.todayKey,
            cards: context.nutrientCards,
            safetySummary: `${context.safetySummary?.title || ""} ${context.safetySummary?.detail || ""}`.trim(),
          },
          selectedFoods,
          knowledgeSnippets,
          appLimits: context.dailyTargets,
        },
      }),
    });

    const payload = await response.json().catch(() => ({}));
    if (payload?.ok && payload.answer) {
      countServerAiCall({
        userType: isGuest ? V2_AI_USER_TYPES.guest : V2_AI_USER_TYPES.registered,
        appSettings: settings?.admin_app_settings || {},
      });
      return sanitizeAssistantText(payload.answer);
    }

    if (payload?.disabled || payload?.reason) {
      return payload.reason || getReadOnlyAssistantFallbackMessage();
    }

    return getReadOnlyAssistantFallbackMessage();
  } catch {
    return getReadOnlyAssistantFallbackMessage();
  }
}

function ChatMessage({ message, lang }) {
  const isUser = message.role === "user";
  const Icon = isUser ? UserRound : Bot;
  const isArabic = lang === "ar";

  return (
    <article className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`} dir={isArabic ? "rtl" : "ltr"}>
      {!isUser && (
        <span className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Icon className="h-4 w-4" />
        </span>
      )}
      <div
        className={`max-w-[min(44rem,82%)] break-words whitespace-pre-line rounded-lg px-4 py-3 text-sm leading-6 sm:max-w-[min(44rem,85%)] ${
          isUser
            ? "bg-primary text-primary-foreground"
            : "border border-border bg-background text-foreground"
        } ${isArabic ? "text-right" : "text-left"}`}
      >
        {message.text}
      </div>
      {isUser && (
        <span className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="h-4 w-4" />
        </span>
      )}
    </article>
  );
}

function createMessage(role, text) {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    role,
    text: sanitizeAssistantText(text),
  };
}
